/**
 * Resume free-text is stored as plain text (see `ResumeEmployment.responsibilities`
 * and `ResumeCustomSection.content`). Nothing in the pipeline that produces it —
 * the PDF/DOCX extractor, the LLM autofill/refine passes, or the builder's
 * textarea — carries a structured list: whatever the user's original resume
 * expressed as a `<ul>` arrives here as newline-separated lines.
 *
 * This module is the single place that decides what those lines *mean*, so the
 * HTML preview, the PDF (rendered from the same HTML), the DOCX export and the
 * TXT export all agree. Every renderer parses the value into blocks here and
 * then emits its own markup for them.
 *
 * The parse is deliberately conservative:
 *
 *   - An explicit marker (`- `, `* `, `• `, `1. `, …) always starts an item.
 *     A line without a marker that follows a marked one is a wrapped
 *     continuation of that item, not a new one.
 *   - Without any markers, a run of two or more consecutive non-blank lines is
 *     read as a list, but only for fields that are semantically item lists
 *     (`autoList`). This is what recovers structure from AI output, which emits
 *     one item per line and no markers.
 *   - Everything else stays a paragraph and is rendered exactly as before.
 *
 * A blank line always ends a run, so genuinely prose content — one long line,
 * or paragraphs separated by blank lines — never turns into bullets.
 */

export interface ListBlock {
  type: 'list';
  ordered: boolean;
  items: string[];
}

export interface ParagraphBlock {
  type: 'paragraph';
  /** Lines as authored, including the blank lines between them. */
  lines: string[];
}

export type ContentBlock = ListBlock | ParagraphBlock;

export interface ParseContentOptions {
  /**
   * Treat a run of 2+ consecutive unmarked lines as a list. Enable for fields
   * that are conceptually a list of items (employment responsibilities, custom
   * sections); leave off for prose fields such as the profile summary, where an
   * explicit marker is required.
   */
  autoList?: boolean;
}

/**
 * Bullet markers. Glyph bullets (`•`, `‣`, …) are unambiguous, so the trailing
 * space is optional; ASCII markers (`-`, `*`, en/em dash) require one, otherwise
 * a hyphenated word or a negative number would read as a bullet.
 */
const UNORDERED_MARKER = /^[ \t]*(?:[•‣▪▫◦·∙]+[ \t]*|[-*][ \t]+|[–—][ \t]+)/;

/** `1.` / `1)` / `(1)` style markers. */
const ORDERED_MARKER = /^[ \t]*\(?\d{1,3}[.)][ \t]+/;

function matchMarker(line: string): { rest: string; ordered: boolean } | null {
  const unordered = line.match(UNORDERED_MARKER);
  if (unordered) {
    return { rest: line.slice(unordered[0].length).trim(), ordered: false };
  }
  const ordered = line.match(ORDERED_MARKER);
  if (ordered) {
    return { rest: line.slice(ordered[0].length).trim(), ordered: true };
  }
  return null;
}

/** Does this value contain anything a renderer should treat as a list? */
export function hasListContent(text: string, options: ParseContentOptions = {}): boolean {
  return parseContentBlocks(text, options).some((block) => block.type === 'list');
}

function isBlank(line: string): boolean {
  return line.trim().length === 0;
}

/**
 * Split one run of consecutive non-blank lines into blocks.
 *
 * With markers present, lines before the first marker are a lead-in paragraph
 * and each unmarked line after a marker extends the item it follows — that is
 * how a bullet that wrapped onto a second line survives a round trip through a
 * plain-text field.
 */
function parseRun(run: string[], autoList: boolean): ContentBlock[] {
  const firstMarkerIndex = run.findIndex((line) => matchMarker(line) !== null);

  if (firstMarkerIndex === -1) {
    if (autoList && run.length >= 2) {
      return [{ type: 'list', ordered: false, items: run.map((line) => line.trim()) }];
    }
    return [{ type: 'paragraph', lines: run }];
  }

  const blocks: ContentBlock[] = [];
  if (firstMarkerIndex > 0) {
    blocks.push({ type: 'paragraph', lines: run.slice(0, firstMarkerIndex) });
  }

  const items: string[] = [];
  let ordered: boolean | null = null;

  for (const line of run.slice(firstMarkerIndex)) {
    const marker = matchMarker(line);
    if (marker) {
      if (ordered === null) ordered = marker.ordered;
      items.push(marker.rest);
      continue;
    }
    // Continuation of the previous item.
    items[items.length - 1] = `${items[items.length - 1]} ${line.trim()}`.trim();
  }

  blocks.push({
    type: 'list',
    ordered: ordered ?? false,
    items: items.filter((item) => item.length > 0),
  });

  return blocks;
}

/** Parse a plain-text field into paragraph and list blocks. */
export function parseContentBlocks(text: string, options: ParseContentOptions = {}): ContentBlock[] {
  const autoList = options.autoList ?? false;
  const normalized = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  if (!normalized.trim()) return [];

  const lines = normalized.split('\n');
  const blocks: ContentBlock[] = [];
  let run: string[] = [];
  let gap: string[] = [];

  const flushRun = () => {
    if (run.length === 0) return;
    const parsed = parseRun(run, autoList);
    // Blank lines that separated this run from the previous one belong to the
    // preceding paragraph, so prose spacing is preserved byte for byte.
    const last = blocks[blocks.length - 1];
    if (gap.length > 0 && last?.type === 'paragraph' && parsed[0]?.type === 'paragraph') {
      last.lines.push(...gap, ...parsed[0].lines);
      blocks.push(...parsed.slice(1));
    } else {
      blocks.push(...parsed);
    }
    run = [];
    gap = [];
  };

  for (const line of lines) {
    if (isBlank(line)) {
      flushRun();
      gap.push(line);
      continue;
    }
    run.push(line);
  }
  flushRun();

  return blocks;
}

/* -------------------------------------------------------------------------- *
 * Rendering
 * -------------------------------------------------------------------------- */

/**
 * Minimal HTML escaping. Mirrors what `Handlebars.escapeExpression` does, so a
 * value that is not a list renders byte-identically to the legacy path.
 */
const HTML_ESCAPES: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#x27;',
  '`': '&#x60;',
  '=': '&#x3D;',
};

export function escapeHtml(value: string): string {
  return value.replace(/[&<>"'`=]/g, (char) => HTML_ESCAPES[char]);
}

/** `true` when the value already carries rich-text markup we must not re-escape. */
export function looksLikeHtml(value: string): boolean {
  return /<\/?[a-z][\s\S]*>/i.test(value);
}

/** Legacy rendering: escape, then keep authored line breaks as `<br/>`. */
function renderParagraphHtml(lines: string[]): string {
  return escapeHtml(lines.join('\n')).replace(/\n/g, '<br/>');
}

function renderListHtml(block: ListBlock): string {
  const tag = block.ordered ? 'ol' : 'ul';
  const items = block.items.map((item) => `<li>${escapeHtml(item)}</li>`).join('');
  return `<${tag} class="content-list">${items}</${tag}>`;
}

/**
 * Render a plain-text resume field to HTML.
 *
 * Returns `null` when the value holds no list, so callers can fall through to
 * whatever they did before and prose keeps rendering exactly as it does today.
 */
export function renderContentBlocksHtml(text: string, options: ParseContentOptions = {}): string | null {
  const blocks = parseContentBlocks(text, options);
  if (!blocks.some((block) => block.type === 'list')) return null;

  return blocks
    .map((block) => (block.type === 'list' ? renderListHtml(block) : renderParagraphHtml(block.lines)))
    .join('');
}

/**
 * Flatten blocks back to plain text lines, list items prefixed with a marker.
 * Used by the TXT export so it shows the same structure the HTML preview does.
 */
export function renderContentBlocksText(
  text: string,
  options: ParseContentOptions & { bullet?: string; indent?: string } = {}
): string[] {
  const bullet = options.bullet ?? '•';
  const indent = options.indent ?? '  ';

  return parseContentBlocks(text, options).flatMap((block) => {
    if (block.type === 'paragraph') return block.lines;
    return block.items.map((item, index) =>
      block.ordered ? `${indent}${index + 1}. ${item}` : `${indent}${bullet} ${item}`
    );
  });
}
